import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { type EventApplicationModuleSpecsFragment } from '../../../../api/fragments/EventApplicationModuleSpecs';
import { ModuleType } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import CascaderField from '../../../../components/fields/CascaderField';
import InputField from '../../../../components/fields/InputField';
import SelectField from '../../../../components/fields/SelectField';
import SwitchField from '../../../../components/fields/SwitchField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useModuleOptions from '../modules/implementations/shared/useModuleOptions';
import { type FormValues } from './EventApplicationModuleMainDetails';

type EventApplicationModuleProps = {
    module: EventApplicationModuleSpecsFragment;
};

const EventApplicationModuleMainDetailsForm = ({ module }: EventApplicationModuleProps) => {
    const { t } = useTranslation(['moduleDetails']);
    const company = useCompany(true);
    const hasCapModule = company?.modules.find(module => module.__typename === ModuleType.CapModule);

    const { yesNoSwitch } = useSystemSwitchData();
    const { values } = useFormikContext<FormValues>();
    const options = useModuleOptions(module.company, { addNoneOptions: { promoCodes: true } });

    return (
        <CollapsibleWrapper defaultActiveKey="Main Details">
            <Panel key="Main Details" header="Main Details">
                <Row gutter={10}>
                    <Col md={8} xs={24}>
                        <InputField
                            {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                            name="displayName"
                            required
                        />
                    </Col>
                    <Col md={8} xs={24}>
                        <SwitchField
                            {...yesNoSwitch}
                            {...t('moduleDetails:fields.isOcrEnabled', { returnObjects: true })}
                            name="isOcrEnabled"
                        />
                    </Col>

                    <Col md={8} xs={24}>
                        <SelectField
                            {...t('moduleDetails:fields.appointmentModuleId', { returnObjects: true })}
                            name="appointmentModuleId"
                            options={options.appointments}
                            showSearch
                        />
                    </Col>

                    <Col md={8} xs={24}>
                        <CascaderField
                            {...t('moduleDetails:fields.liveChatSetting', { returnObjects: true })}
                            name="liveChatSettingId"
                            options={options.liveChat}
                            showSearch
                        />
                    </Col>

                    {values.appointmentModuleId && (
                        <Col md={8} xs={24}>
                            <SwitchField
                                {...yesNoSwitch}
                                {...t('moduleDetails:fields.displayAppointmentDatepicker', { returnObjects: true })}
                                name="displayAppointmentDatepicker"
                            />
                        </Col>
                    )}

                    <Col md={8} xs={24}>
                        <SwitchField
                            {...yesNoSwitch}
                            {...t('moduleDetails:fields.showResetKYCButton', { returnObjects: true })}
                            name="showResetKYCButton"
                        />
                    </Col>

                    {hasCapModule && (
                        <Col md={8} xs={24}>
                            <SwitchField
                                {...yesNoSwitch}
                                {...t('moduleDetails:fields.isCapEnabled', { returnObjects: true })}
                                name="isCapEnabled"
                            />
                        </Col>
                    )}
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default EventApplicationModuleMainDetailsForm;
